// src/lib/guided/compile.ts
//
// Phase 2 of docs/guided-shopping_plan.md: `compileFunnel` turns a
// `FunnelConfig` into a hidden-step flow -- one field per criterion plus a
// synthetic "what do you need?" opener (research §6.3 Q1), each field's
// `FieldLogic` true exactly on the path that reaches it (2.4), and the
// `FormOutcome`s a path can end on (2.2 criteria-to-fields, 2.3's
// information-gain ordering in `order.ts`, 2.4 hidden steps and outcomes).
//
// Resolving a *submitted* set of answers to the outcome it reaches lives in
// `outcomes.ts`, not here: it needs none of the step-building machinery,
// only the same offer-survivor rule (`order.ts`'s `survivorsForBucket`) and
// the synthetic "need" criterion this file defines.
//
// 2.5 adds `lints.ts`: `compileFunnel` runs every compiled flow through
// `lintFunnel` before returning it and throws on any error-level §3.14
// finding, so a flow that fails a hard rule (a countdown, a pre-selected
// option, an implication question) is never handed to a caller. This makes
// `compile.ts` <-> `lints.ts` a fourth module in the same deliberate import
// cycle as `compile.ts` <-> `order.ts` (2.3) and `compile.ts` <-> `outcomes.ts`
// (2.4): safe under Node ESM only because every cross-module reference sits
// inside a function body, never at either module's own top level (see
// `.wolf/cerebrum.md`'s 2026-09-24 entry on this exact trap).
//
// 2.9 attaches `config.concerns` to every compiled question field as
// metadata (`TypedFormField.concerns`, research §6.4), never as a step of
// its own -- the hidden-step logic and the path invariants (2.4, 2.6) never
// see it, since it plays no part in `planSteps` or `FieldLogic`. `lints.ts`
// separately checks the concern answers themselves against §8's "claims we
// must not make".
import type {
  CriterionType,
  FunnelConfig,
  FunnelCriterion,
  FunnelOfferFit,
} from "./config";
import type {
  FieldLogic,
  FieldOption,
  FormOutcome,
  LogicCondition,
  TypedFormField,
} from "@/lib/types";
import { chooseNextCriterion, survivorsForBucket } from "./order";
import { buildFunnelOutcomes } from "./outcomes";
import { lintFunnel } from "./lints";

export interface CompiledFunnel {
  fields: TypedFormField[];
  outcomes: FormOutcome[];
}

/**
 * `timing`, `criticalEvent`, `trigger` and `stage`: plan 1.1's "the urgency
 * family". Exported so 2.3's ordering and 2.5's lints share one definition
 * instead of each re-deriving it from the plan text.
 */
export const URGENCY_FAMILY_TYPES: readonly CriterionType[] = [
  "timing",
  "criticalEvent",
  "trigger",
  "stage",
];

/** §3.14 rule 3: factual qualifiers get a "Not sure yet" option. */
export const NOT_SURE_YET_OPTION: FieldOption = {
  label: "Not sure yet",
  value: "not-sure-yet",
};

/** §3.14 rule 4: budget position is a soft constraint, with this option. */
export const PREFER_TO_DISCUSS_OPTION: FieldOption = {
  label: "Prefer to discuss",
  value: "prefer-to-discuss",
};

/**
 * Compile-time id for the "what do you need?" opener (2.4, research §6.3
 * Q1): one radio field, one option per configured offer, always the first
 * step whenever there is more than one offer to pick between. Reserved
 * rather than owner-assignable -- it never appears in `FunnelConfig.criteria`,
 * and `compileFunnel` throws if an owner criterion collides with it.
 */
export const NEED_CRITERION_ID = "__need__";

/**
 * The urgency family plus `money`: §3.14 rule 3's "factual qualifiers".
 * Exported for 2.5's `lints.ts`, which checks the same "Not sure yet is
 * factual-only" rule against a compiled field's originating criterion.
 */
export function isFactualCriterion(type: CriterionType): boolean {
  return URGENCY_FAMILY_TYPES.includes(type) || type === "money";
}

/**
 * A bucket becomes an option (plan 2.2), plus the §3.14 rule-3/rule-4
 * synthetic options: "Not sure yet" on every factual criterion, "Prefer to
 * discuss" on `money` only. Skipped when the owner already wrote a bucket
 * with the same label -- the shipped "considered-purchase" money preset
 * (presets.ts MONEY_CRITERION) already writes its own "Not sure yet"
 * bucket, and a second button with a different id but the same text would
 * read as a duplicate choice, not a new one.
 */
function optionsForCriterion(criterion: FunnelCriterion): FieldOption[] {
  const options: FieldOption[] = criterion.buckets.map((bucket) => ({
    label: bucket.label,
    value: bucket.id,
  }));

  const hasLabel = (label: string): boolean =>
    options.some(
      (option) =>
        typeof option.label === "string" &&
        option.label.trim().toLowerCase() === label.toLowerCase(),
    );

  if (
    isFactualCriterion(criterion.type) &&
    !hasLabel(NOT_SURE_YET_OPTION.label)
  ) {
    options.push(NOT_SURE_YET_OPTION);
  }
  if (criterion.type === "money" && !hasLabel(PREFER_TO_DISCUSS_OPTION.label)) {
    options.push(PREFER_TO_DISCUSS_OPTION);
  }

  return options;
}

/**
 * Gate criteria first (plan 2.2, research §3a.6 step 2: "hard gates, if
 * any" come right after the opener). Everything else keeps its configured
 * order. Used two ways: 2.3's `order.ts` never calls it (it orders by
 * information gain); 2.4's `planSteps` below falls back to it once nothing
 * left discriminates between the remaining offers.
 */
function orderCriteria(criteria: FunnelCriterion[]): FunnelCriterion[] {
  return [
    ...criteria.filter((criterion) => criterion.type === "gate"),
    ...criteria.filter((criterion) => criterion.type !== "gate"),
  ];
}

/**
 * §3.14 rule 6 / plan 2.4: "one clause naming a concrete benefit, on by
 * default only for budget[...] questions" (the research's "contact"
 * half has no compiled field yet -- contact is Phase 5). Rendered as the
 * field's `helpText`, the existing free-text description slot every
 * renderer already shows.
 */
function whyWeAskFor(criterion: FunnelCriterion): string | null {
  if (criterion.type !== "money") return null;
  return "We ask so we can point you to the right option for your budget.";
}

/**
 * A field with no `formId`/`stepId`: `compileFunnel` is pure and knows
 * nothing about which form it will be attached to or that guided flows have
 * no `FormStep` rows (research §6.3, "hidden steps" on one ordered list).
 * The caller that persists a compiled flow stamps in the real `formId`.
 */
function fieldForCriterion(
  criterion: FunnelCriterion,
  order: number,
  logic: FieldLogic | null,
  concerns: FunnelConfig["concerns"],
): TypedFormField {
  return {
    id: criterion.id,
    formId: "",
    stepId: null,
    type: "radio",
    name: criterion.id,
    label: criterion.question,
    required: true,
    order,
    logic,
    placeholder: null,
    validation: null,
    options: optionsForCriterion(criterion),
    helpText: whyWeAskFor(criterion),
    defaultValue: null,
    className: null,
    readOnly: false,
    disabled: false,
    multiple: false,
    concerns,
  };
}

/**
 * Exported for 3.3 (`explain.ts`): explaining which answers decided an
 * outcome has to walk the identical narrowing order `resolveOutcome` used to
 * reach it, including this synthetic criterion, rather than a second
 * "what do you need help with?" definition that could drift from this one.
 */
export function buildNeedCriterion(
  offers: FunnelConfig["offers"],
): FunnelCriterion {
  return {
    id: NEED_CRITERION_ID,
    question: "What do you need help with?",
    buckets: offers.map((offer) => ({ id: offer.id, label: offer.name })),
    source: "Derived from the configured offers (research §6.3 Q1)",
    type: "gate",
  };
}

/**
 * `config.offerFit` plus one synthetic entry per offer: an offer survives
 * the "need" opener only on its own bucket. Exported so `outcomes.ts` can
 * apply the identical survivor rule when resolving a submitted "need"
 * answer, rather than re-deriving this mapping a second way.
 */
export function effectiveOfferFit(config: FunnelConfig): FunnelOfferFit {
  if (config.offers.length <= 1) return config.offerFit;
  const merged: FunnelOfferFit = { ...config.offerFit };
  for (const offer of config.offers) {
    merged[offer.id] = { ...merged[offer.id], [NEED_CRITERION_ID]: [offer.id] };
  }
  return merged;
}

/**
 * Does any offer's `offerFit` entry mention this criterion at all? Exported
 * for 7.2 (`proposals.ts`): a criterion no offer's fit mentions cannot
 * affect which offer is reached, so it is the one provable case where
 * removing it changes only the grade (§3.14 rule 14's first preference),
 * the same predicate `lints.ts`'s `lintDataMinimization` already uses for
 * its narrower `fit`-only warning.
 */
export function offerFitMentionsCriterion(
  criterionId: string,
  offerFit: FunnelOfferFit,
): boolean {
  return Object.values(offerFit).some((entry) => criterionId in entry);
}

/**
 * Whether `criterion` is still worth asking given `remainingOfferIds`:
 * either no offer at all cares about it (a universal question like
 * `timing` in the `WEB_AGENCY_CONFIG` fixture, asked regardless of which
 * offer was picked), or at least one still-possible offer does. A
 * criterion that only ever mattered to an offer already ruled out is
 * skipped. This is 2.4's half of the split order.ts's own header comment
 * describes: "2.4 owns deciding how a criterion relevant to exactly one
 * already-chosen offer still gets asked once the offer picker has narrowed
 * to it."
 */
function isRelevantToOffers(
  criterion: FunnelCriterion,
  remainingOfferIds: readonly string[],
  offerFit: FunnelOfferFit,
): boolean {
  if (!offerFitMentionsCriterion(criterion.id, offerFit)) return true;
  return remainingOfferIds.some(
    (offerId) => criterion.id in (offerFit[offerId] ?? {}),
  );
}

/** One edge in the compiled step tree: what has to be true for a step to show. */
interface FieldTrigger {
  parentFieldId: string;
  /**
   * A specific required answer, or `null` for "the parent has been
   * answered at all" -- every bucket of the parent leads to the same next
   * step, so the condition only needs to know the parent was answered, not
   * which of its buckets was chosen.
   */
  bucketId: string | null;
}

function triggerKey(trigger: FieldTrigger): string {
  return `${trigger.parentFieldId}:${trigger.bucketId ?? "*"}`;
}

/**
 * A field reached the same way twice (2.2's "places gate criteria first"
 * fixture asks `service-area` on both the website and marketing branches)
 * gets one merged trigger list, not two fields: `conditionType: "any"` over
 * the union of every path's trigger is exactly "show on any path that
 * reaches this step". Conditions are built only from
 * `evaluateCondition`'s own operators (`equals`, `is_not_empty`), per
 * plan 2.4.
 */
function buildFieldLogic(triggers: FieldTrigger[]): FieldLogic | null {
  if (triggers.length === 0) return null;
  const conditions: LogicCondition[] = triggers.map((trigger) =>
    trigger.bucketId === null
      ? {
          id: triggerKey(trigger),
          fieldId: trigger.parentFieldId,
          operator: "is_not_empty",
          value: "",
        }
      : {
          id: triggerKey(trigger),
          fieldId: trigger.parentFieldId,
          operator: "equals",
          value: trigger.bucketId,
        },
  );
  return { action: "show", conditionType: "any", conditions };
}

function survivorSetsEqual(
  a: readonly string[],
  b: readonly string[],
): boolean {
  if (a.length !== b.length) return false;
  const bSet = new Set(b);
  return a.every((id) => bSet.has(id));
}

interface StepPlan {
  /** Criterion ids, in first-encountered order -- this becomes field order. */
  order: string[];
  criteriaById: Map<string, FunnelCriterion>;
  triggersById: Map<string, FieldTrigger[]>;
}

/**
 * Walks the same offer-narrowing state order.ts's `chooseOrderNode` does
 * (reusing its exported `chooseNextCriterion` directly, candidates plus the
 * synthetic "need" criterion when there is more than one offer), extended
 * for 2.4: where that function stops -- one offer left, or nothing
 * discriminates -- this keeps asking whatever `isRelevantToOffers`
 * criteria remain, gate-first (`orderCriteria`), so `pages`/`budget` still
 * get asked on their one relevant branch even though neither ever
 * separates offers (order.ts's own header comment on the
 * `WEB_AGENCY_CONFIG` fixture explains why they can't).
 *
 * A chosen criterion's own narrowing effect (`survivorsForBucket`) is
 * always applied, whichever phase picked it: the distinction between the
 * two phases is only how the next criterion is chosen, never whether its
 * answer can still rule an offer out. That is what lets a later real gate
 * eliminate an offer the opener already pointed at (survivors drop to
 * zero, `outcomes.ts` reads that as no offer surviving).
 */
function planSteps(config: FunnelConfig): StepPlan {
  const offerFit = effectiveOfferFit(config);
  const needCriterion =
    config.offers.length > 1 ? buildNeedCriterion(config.offers) : null;
  const allOfferIds = config.offers.map((offer) => offer.id);

  const order: string[] = [];
  const criteriaById = new Map<string, FunnelCriterion>();
  const triggersById = new Map<string, FieldTrigger[]>();

  function registerTrigger(
    criterionId: string,
    trigger: FieldTrigger | null,
  ): void {
    if (!trigger) return;
    const existing = triggersById.get(criterionId) ?? [];
    if (!existing.some((t) => triggerKey(t) === triggerKey(trigger))) {
      existing.push(trigger);
    }
    triggersById.set(criterionId, existing);
  }

  function visit(
    remainingOfferIds: readonly string[],
    askedIds: ReadonlySet<string>,
    depth: number,
    incomingTrigger: FieldTrigger | null,
  ): void {
    if (depth >= config.maxQuestions) return;
    if (remainingOfferIds.length === 0) return;

    const unaskedCriteria = config.criteria.filter((c) => !askedIds.has(c.id));
    const candidates =
      needCriterion && !askedIds.has(needCriterion.id)
        ? [...unaskedCriteria, needCriterion]
        : unaskedCriteria;

    let chosen = chooseNextCriterion(candidates, remainingOfferIds, offerFit);
    if (!chosen) {
      chosen =
        orderCriteria(config.criteria).find(
          (c) =>
            !askedIds.has(c.id) &&
            isRelevantToOffers(c, remainingOfferIds, offerFit),
        ) ?? null;
    }
    if (!chosen) return;

    registerTrigger(chosen.id, incomingTrigger);
    if (!criteriaById.has(chosen.id)) {
      criteriaById.set(chosen.id, chosen);
      order.push(chosen.id);
    }

    const nextAsked = new Set(askedIds);
    nextAsked.add(chosen.id);

    const perBucket = chosen.buckets.map((bucket) => ({
      bucket,
      survivors: survivorsForBucket(
        chosen!.id,
        bucket.id,
        remainingOfferIds,
        offerFit,
      ),
    }));
    const uniform = perBucket.every((entry) =>
      survivorSetsEqual(entry.survivors, perBucket[0].survivors),
    );

    if (uniform) {
      visit(perBucket[0].survivors, nextAsked, depth + 1, {
        parentFieldId: chosen.id,
        bucketId: null,
      });
    } else {
      for (const { bucket, survivors } of perBucket) {
        visit(survivors, nextAsked, depth + 1, {
          parentFieldId: chosen.id,
          bucketId: bucket.id,
        });
      }
    }
  }

  visit(allOfferIds, new Set(), 0, null);

  return { order, criteriaById, triggersById };
}

/**
 * Configuration to compiled flow (Phase 2). Pure and deterministic: the
 * same `config` produces the same fields, byte for byte -- nothing here
 * reads a clock, a random source or the network. Field `id` and `name` are
 * the criterion's own id, both because that is already the stable,
 * owner-assigned identity and because it makes a `FieldLogic` condition
 * referencing "the answer to this criterion" a direct lookup rather than a
 * second id scheme to keep in sync.
 */
export function compileFunnel(config: FunnelConfig): CompiledFunnel {
  const seenCriterionIds = new Set<string>();
  for (const criterion of config.criteria) {
    if (criterion.id === NEED_CRITERION_ID) {
      throw new Error(
        `Criterion id "${NEED_CRITERION_ID}" is reserved for the offers picker`,
      );
    }
    if (seenCriterionIds.has(criterion.id)) {
      throw new Error(`Duplicate criterion id: "${criterion.id}"`);
    }
    seenCriterionIds.add(criterion.id);
  }

  const plan = planSteps(config);
  // `?? []`, not a direct read: `compileFunnel` takes a plain `FunnelConfig`
  // rather than a `parseFunnelConfig` result (2.1's own note on the empty-
  // criteria-list test), so a hand-built fixture that predates 1.4 can omit
  // `concerns` entirely despite the zod schema defaulting it.
  const concerns = config.concerns ?? [];
  const fields = plan.order.map((criterionId, index) =>
    fieldForCriterion(
      plan.criteriaById.get(criterionId)!,
      index,
      buildFieldLogic(plan.triggersById.get(criterionId) ?? []),
      concerns,
    ),
  );

  const compiled: CompiledFunnel = {
    fields,
    outcomes: buildFunnelOutcomes(config),
  };

  // 2.5: the compiler refuses to return a flow that fails an error-level
  // §3.14 lint. Warnings are surfaced to the owner (Phase 4), not blocked.
  const errors = lintFunnel(config, compiled).filter(
    (f) => f.level === "error",
  );
  if (errors.length > 0) {
    throw new Error(
      `Compiled flow fails ${errors.length} lint rule(s): ${errors
        .map((f) => f.message)
        .join("; ")}`,
    );
  }

  return compiled;
}
